<script setup lang="ts">
import { computed } from 'vue'

import AppDatePicker from '@/components/AppDatePicker.vue'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Operator } from '@/types/reports'

const props = defineProps<{
  operator: Operator | null
  value: unknown
}>()

const emit = defineEmits<{ update: [{ operator: Operator; value: unknown }] }>()

interface DateChoice {
  key: string
  label: string
  operator: Operator
  value: unknown
}

const PERIODS: DateChoice[] = [
  { key: 'last_7d', label: 'Last 7 days', operator: 'between', value: ['-7d', 'today'] },
  { key: 'last_30d', label: 'Last 30 days', operator: 'between', value: ['-30d', 'today'] },
  { key: 'last_90d', label: 'Last 90 days', operator: 'between', value: ['-90d', 'today'] },
  { key: 'last_6m', label: 'Last 6 months', operator: 'between', value: ['-6m', 'today'] },
  { key: 'last_12m', label: 'Last 12 months', operator: 'between', value: ['-12m', 'today'] },
  { key: 'this_month', label: 'This month', operator: 'between', value: ['month_start', 'today'] },
  { key: 'this_fy', label: 'This financial year', operator: 'between', value: ['fy_start', 'today'] },
]

const EXACT: DateChoice[] = [
  { key: 'custom_range', label: 'Custom range…', operator: 'between', value: [null, null] },
  { key: 'equals', label: 'On', operator: 'equals', value: null },
  { key: 'not_equals', label: 'Not on', operator: 'not_equals', value: null },
  { key: 'gte', label: 'On or after', operator: 'gte', value: null },
  { key: 'lte', label: 'On or before', operator: 'lte', value: null },
  { key: 'gt', label: 'After', operator: 'gt', value: null },
  { key: 'lt', label: 'Before', operator: 'lt', value: null },
]

const RELATIVE_PATTERN = /^(today|month_start|fy_start|-\d+[dmy])$/

const MIN_DATE = '2004-01-01'
const TODAY = new Date().toISOString().split('T')[0]

const bounds = computed<[string, string]>(() =>
  Array.isArray(props.value) ? [(props.value[0] as string) ?? '', (props.value[1] as string) ?? ''] : ['', ''],
)

const single = computed(() => (typeof props.value === 'string' ? props.value : ''))

const selectedKey = computed(() => {
  if (!props.operator) return undefined
  if (props.operator === 'between') {
    const [from, to] = bounds.value
    return (
      PERIODS.find((p) => {
        const [pf, pt] = p.value as [string, string]
        return pf === from && pt === to
      })?.key ?? 'custom_range'
    )
  }
  return props.operator
})

function choose(key: string) {
  const choice = [...PERIODS, ...EXACT].find((c) => c.key === key)
  if (!choice) return
  emit('update', { operator: choice.operator, value: Array.isArray(choice.value) ? [...choice.value] : choice.value })
}

const showRange = computed(() => selectedKey.value === 'custom_range')
const showSingle = computed(() => !!props.operator && props.operator !== 'between')

function isRelative(value: string) {
  return RELATIVE_PATTERN.test(value)
}

function setBound(index: 0 | 1, value: string) {
  const next: [string, string] = [...bounds.value]
  next[index] = value
  emit('update', { operator: 'between', value: next })
}

function setSingle(value: string) {
  emit('update', { operator: props.operator!, value })
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <Select :model-value="selectedKey" @update:model-value="(v) => choose(v as string)">
      <SelectTrigger class="w-44">
        <SelectValue placeholder="When…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="choice in PERIODS" :key="choice.key" :value="choice.key">{{ choice.label }}</SelectItem>
        <SelectSeparator />
        <SelectItem v-for="choice in EXACT" :key="choice.key" :value="choice.key">{{ choice.label }}</SelectItem>
      </SelectContent>
    </Select>

    <template v-if="showRange">
      <template v-for="index in [0, 1] as const" :key="index">
        <span v-if="index === 1" class="text-muted-foreground self-center text-sm">and</span>
        <Input
          v-if="isRelative(bounds[index])"
          :model-value="bounds[index]"
          class="w-28"
          :placeholder="index === 0 ? 'From' : 'To'"
          @update:model-value="(v) => setBound(index, String(v))"
        />
        <AppDatePicker
          v-else
          :model-value="bounds[index]"
          :placeholder="index === 0 ? 'From' : 'To'"
          :min="index === 1 && !isRelative(bounds[0]) ? bounds[0] || MIN_DATE : MIN_DATE"
          :max="index === 0 && !isRelative(bounds[1]) ? bounds[1] || TODAY : TODAY"
          class="w-auto min-w-40"
          @update:model-value="(v) => setBound(index, v)"
        />
      </template>
    </template>

    <template v-else-if="showSingle">
      <Input
        v-if="isRelative(single)"
        :model-value="single"
        class="w-28"
        @update:model-value="(v) => setSingle(String(v))"
      />
      <AppDatePicker
        v-else
        :model-value="single"
        :min="MIN_DATE"
        :max="TODAY"
        class="w-auto min-w-40"
        @update:model-value="(v) => setSingle(v)"
      />
    </template>
  </div>
</template>
